import { forwardRef, useId, useMemo, useState } from 'react'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import { AsYouType, getCountries, getCountryCallingCode } from 'libphonenumber-js/min'

/**
 * An international phone number field: a country/dial-code picker fused to a
 * text input, so typing a number never requires knowing — or getting right —
 * the leading "+<code>" by hand.
 *
 * Every country libphonenumber-js has metadata for is offered (245, matching
 * the backend's own App\Support\PhoneNumber support exactly — that class is
 * this component's server-side counterpart: same library family, same
 * "digits with a country code, however typed" model). Display names come
 * from Intl.DisplayNames rather than a hand-maintained table, so the list
 * never drifts out of sync with the codes it is naming.
 *
 * Value shape: a single string, same as TextField's `value`/`onChange`
 * contract — deliberately, so this drops in wherever a bare phone TextField
 * used to be with no payload-shape changes elsewhere. The string is
 * international format as typed (e.g. "+91 98765 43210"); the picker only
 * ever PREFIXES what's typed, it never silently rewrites a number someone
 * already typed with its own "+" and code, matching how the backend's own
 * PhoneNumber::e164() treats an explicit leading "+" as the final word on
 * which country a number belongs to.
 *
 * The country picker can be left uncontrolled (its own internal state,
 * detected from `value` when possible) or controlled via `country`/
 * `onCountryChange` — People.jsx's employee form uses the controlled form,
 * so the SAME Country selection already governing how a bare (no "+") number
 * is read also drives this field's dial-code prefix, rather than the two
 * having separate, potentially contradictory ideas of the country.
 */
export const PhoneField = forwardRef(function PhoneField(
  {
    label,
    hint,
    error,
    required,
    className,
    value,
    onChange,
    country: controlledCountry,
    onCountryChange,
    defaultCountry = 'IN',
    placeholder,
    ...props
  },
  ref,
) {
  const generatedId = useId()
  const id = props.id ?? generatedId

  const [internalCountry, setInternalCountry] = useState(
    controlledCountry ?? detectCountry(value) ?? defaultCountry,
  )

  const country = controlledCountry ?? internalCountry

  function setCountry(next) {
    if (onCountryChange) onCountryChange(next)
    else setInternalCountry(next)
  }

  const countries = useMemo(() => buildCountryList(), [])
  const callingCode = safeCallingCode(country) ?? safeCallingCode(defaultCountry) ?? '91'

  function handleNumberChange(event) {
    const typed = event.target.value

    // A "+" typed directly into the number box states its own country
    // outright — pass it through as-is rather than double-prefixing, the
    // same "an explicit + wins" rule PhoneNumber::e164() applies server-side.
    if (typed.trim().startsWith('+')) {
      onChange?.(typed)
      const detected = detectCountry(typed)
      if (detected && detected !== country) setCountry(detected)
      return
    }

    onChange?.(typed ? `+${callingCode} ${typed}` : '')
  }

  // What the number box itself shows: the part after "+<code> " when the
  // stored value already carries this field's own dial code, otherwise the
  // raw value (covers a value typed with a different "+" code, or a bare
  // legacy value with no code at all — neither should be silently hidden).
  const prefix = `+${callingCode} `
  const numberOnly = value?.startsWith(prefix) ? value.slice(prefix.length) : (value ?? '')

  const describedBy = [hint && !error && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined

  return (
    <div className={clsx('w-full', className)}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-[var(--color-ink-700)]">
        {label}
        {required && <span className="ml-0.5 text-[var(--color-danger)]" aria-hidden="true">*</span>}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      {hint && !error && (
        <p id={`${id}-hint`} className="mb-1.5 text-xs text-[var(--color-ink-500)]">
          {hint}
        </p>
      )}

      <div className="flex gap-2">
        <div className="relative shrink-0">
          <select
            aria-label="Country"
            value={country}
            onChange={(event) => setCountry(event.target.value)}
            className={clsx(
              'h-11 cursor-pointer appearance-none rounded-md border bg-[var(--color-surface)] py-0 pl-2.5 pr-7',
              'text-[0.9375rem] text-[var(--color-ink-900)] outline-none',
              'border-[var(--color-line-strong)] hover:border-[var(--color-ink-400)]',
              'focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/25',
              'w-[6.5rem]',
            )}
          >
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} +{c.callingCode}
              </option>
            ))}
          </select>
          <ChevronDown
            size={14}
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-ink-500)]"
          />
        </div>

        <input
          ref={ref}
          id={id}
          type="tel"
          inputMode="tel"
          value={numberOnly}
          onChange={handleNumberChange}
          placeholder={placeholder ?? 'Number'}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          className={clsx(
            'h-11 min-w-0 flex-1 rounded-md border bg-[var(--color-surface)] px-3',
            'text-[0.9375rem] text-[var(--color-ink-900)] outline-none',
            'transition-colors duration-150',
            error
              ? 'border-[var(--color-danger)] focus:ring-2 focus:ring-[var(--color-danger)]/25'
              : 'border-[var(--color-line-strong)] hover:border-[var(--color-ink-400)] focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/25',
          )}
          {...props}
        />
      </div>

      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  )
})

function safeCallingCode(iso) {
  try {
    return getCountryCallingCode(iso)
  } catch {
    return null
  }
}

function detectCountry(value) {
  if (!value?.trim().startsWith('+')) return null

  try {
    const typer = new AsYouType()
    typer.input(value)
    return typer.getNumber()?.country ?? null
  } catch {
    return null
  }
}

let cachedCountryList = null

function buildCountryList() {
  if (cachedCountryList) return cachedCountryList

  const names = safeDisplayNames()

  cachedCountryList = getCountries()
    .map((code) => ({
      code,
      callingCode: getCountryCallingCode(code),
      name: names?.of(code) ?? code,
    }))
    .sort((a, b) => a.name.localeCompare(b.name))

  return cachedCountryList
}

function safeDisplayNames() {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' })
  } catch {
    return null
  }
}
